import { useEffect, useState, useCallback } from "react";
import { apiClient } from "../App";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Loader2, Activity, RefreshCw } from "lucide-react";

const fmtGBP = (n) => `£${Math.round(n || 0).toLocaleString("en-GB")}`;

export default function SubscriptionHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/platform/subscription-health");
      setData(res.data);
    } catch (e) {
      if (e?.response?.status === 403) setData({ forbidden: true });
      else setData({ error: true });
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return (<Card className="p-4 border-slate-200" data-testid="subscription-health-loading"><div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="h-4 w-4 animate-spin"/>Loading subscription health…</div></Card>);
  if (!data || data.forbidden || data.error) return null;

  const total = data.total || 0;
  const bar = (count, color) => total ? `${(count / total) * 100}%` : "0%";

  return (
    <Card className="p-4 border-slate-200 bg-white" data-testid="subscription-health">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-indigo-600"/>
          <h3 className="font-semibold text-slate-900 text-sm">Subscription Health</h3>
          <span className="text-xs text-slate-500">{total} org{total === 1 ? "" : "s"} · trial-to-paid {data.trialToPaidPct}%</span>
        </div>
        <Button variant="ghost" size="sm" onClick={load} className="h-7 px-2" data-testid="subscription-health-refresh"><RefreshCw className="h-3.5 w-3.5"/></Button>
      </div>

      {/* Funnel bar */}
      <div className="mb-3">
        <div className="flex h-3 rounded-md overflow-hidden border border-slate-100 bg-slate-50">
          <div className="bg-amber-400" style={{ width: bar(data.trial) }} title={`Trial: ${data.trial}`}/>
          <div className="bg-emerald-500" style={{ width: bar(data.activePaid) }} title={`Paid: ${data.activePaid}`}/>
          <div className="bg-rose-400" style={{ width: bar(data.pastDue) }} title={`Past due: ${data.pastDue}`}/>
          <div className="bg-slate-400" style={{ width: bar(data.churned) }} title={`Churned: ${data.churned}`}/>
        </div>
        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5 text-[10px] text-slate-600">
          <span className="flex items-center gap-1"><span className="h-2 w-2 bg-amber-400 rounded-sm"/>Trial {data.trial}</span>
          <span className="flex items-center gap-1"><span className="h-2 w-2 bg-emerald-500 rounded-sm"/>Paid {data.activePaid}</span>
          <span className="flex items-center gap-1"><span className="h-2 w-2 bg-rose-400 rounded-sm"/>Past-due {data.pastDue}</span>
          <span className="flex items-center gap-1"><span className="h-2 w-2 bg-slate-400 rounded-sm"/>Churned {data.churned}</span>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="rounded-md p-2 bg-emerald-50 border border-emerald-200" data-testid="sub-mrr-paid">
          <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">MRR · Paid</div>
          <div className="text-sm font-bold text-emerald-900">{fmtGBP(data.mrrFromPaidGBP)}</div>
        </div>
        <div className="rounded-md p-2 bg-rose-50 border border-rose-200" data-testid="sub-mrr-risk">
          <div className="text-[10px] uppercase tracking-wider text-rose-700 font-semibold">MRR · At risk</div>
          <div className="text-sm font-bold text-rose-900">{fmtGBP(data.mrrAtRiskGBP)}</div>
        </div>
        <div className="rounded-md p-2 bg-slate-50 border border-slate-200" data-testid="sub-churn-rate">
          <div className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">Churn rate</div>
          <div className="text-sm font-bold text-slate-900">{data.churnRatePct}%</div>
        </div>
      </div>

      {/* Stale trials + past due */}
      {(data.staleTrialsCount > 0 || (data.pastDueOrgs || []).length > 0) && (
        <div className="space-y-1.5">
          {data.staleTrialsCount > 0 && (
            <div className="p-2 rounded-md bg-amber-50 border border-amber-200" data-testid="sub-stale-trials">
              <div className="text-[10px] uppercase tracking-wider text-amber-800 font-semibold mb-0.5">{data.staleTrialsCount} stale trial{data.staleTrialsCount === 1 ? "" : "s"} · ≥14d</div>
              <div className="flex flex-wrap gap-1">
                {(data.staleTrials || []).map(o => <Badge key={o.id} className="bg-amber-100 text-amber-900 border border-amber-300 text-[10px] px-1.5 py-0">{o.name} · {o.ageDays}d</Badge>)}
              </div>
            </div>
          )}
          {(data.pastDueOrgs || []).length > 0 && (
            <div className="p-2 rounded-md bg-rose-50 border border-rose-200" data-testid="sub-past-due">
              <div className="text-[10px] uppercase tracking-wider text-rose-800 font-semibold mb-0.5">Past-due — recover urgently</div>
              <div className="flex flex-wrap gap-1">
                {(data.pastDueOrgs || []).map(o => <Badge key={o.id} className="bg-rose-100 text-rose-900 border border-rose-300 text-[10px] px-1.5 py-0">{o.name} · {fmtGBP(o.mrrGBP)}</Badge>)}
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
